import type {
    FilmoscopeApi,
    FilmoscopeEndedEventContext,
    RequiredParamsForAnyPlayback,
    SDKContentParams,
    LegacySDKPlayback,
} from '@kinopoisk-int/filmoscope';

import { DeepReadonly } from '@/scenarios/legacy-player/lib/deepReadonly';

import { AutoplayPolicy, FetchSourceCallback, Source, Stream } from './config';

export type PlayerAdapter = FilmoscopeAdapter;

interface BasePlayerAdapter {
    type: 'filmoscope';
    // Mute
    readonly isMuted: boolean;
    setIsMuted(isMuted: boolean): void;
    onMuteChange(callback: (isMuted: boolean) => void): () => void;

    // VideoStretching
    stretchVideoToContainerSize(): void;

    // CurrentTime
    readonly currentTime: number | undefined;
    onCurrentTimeChange(callback: (currentTime: number) => void): () => void;

    // Duration
    readonly duration: number | undefined;
    onDurationChange(callback: (duration: number) => void): () => void;

    // Error
    readonly hasError: boolean;
    onError(callback: (error: PlayerApiError) => void): () => void;

    // PlayingState
    readonly playingState: PlayingState | undefined;
    onPlayingStateChange(callback: (state: PlayingState) => void): () => void;
    onFinish(callback: FinishEventListener): () => void;
    play(): void;
    pause(): void;

    // ContentImpression
    onContentImpression(callback: () => void): () => void;

    // startStage
    readonly isStarted: boolean;
    readonly isWaitingUserPlay: boolean;
    onIsStartedChange(callback: (isStarted: boolean) => void): () => void;
    onWaitingUserPlayChange(callback: (isWaitingUserPlay: boolean) => void): () => void;
    onReady(callback: () => void): () => void;

    // Seek
    seek(second: number): void;

    // VideoSize
    readonly videoSize: VideoSize | undefined;
    onVideoSizeChange(callback: (videoSize: VideoSize | undefined) => void): () => void;

    // Volume
    readonly volume: number | undefined;
    setVolume(volumeLevel: number): void;
    onVolumeChange(callback: (volume: number) => void): () => void;

    // Source
    readonly source: DeepReadonly<Source> | undefined;
    setSource(source: Source | Source[]): void;
    onSourceChange(callback: (newSource: DeepReadonly<Source> | undefined) => void): () => void;
    playNextSource(): void;

    // Stream
    readonly currentStream: DeepReadonly<Omit<Stream, 'drmConfig'>> | undefined;
    onCurrentStreamChange(
        callback: (currentStream: DeepReadonly<Omit<Stream, 'drmConfig'>> | undefined) => void
    ): () => void;

    // WatchedTime
    readonly watchedTime: number | undefined;
    onWatchedTimeChange(callback: (watchedTime: number) => void): () => void;

    // AudioTrack
    readonly audioTrack: AudioTrack | undefined;
    readonly audioTracks: ReadonlyArray<AudioTrack> | undefined;
    setAudioTrack(track: AudioTrack): void;
    onCurrentAudioTrackChange(callback: (audioTrack?: AudioTrack) => void): () => void;
    onAudioTracksChange(callback: (audioTracks?: ReadonlyArray<AudioTrack>) => void): () => void;

    // TextTrack
    readonly textTrack: TextTrack | undefined;
    readonly textTracks: ReadonlyArray<TextTrack> | undefined;
    setTextTrack(track?: TextTrack): void;
    onCurrentTextTrackChange(callback: (textTrack?: TextTrack) => void): () => void;
    onTextTracksChange(callback: (textTracks?: ReadonlyArray<TextTrack>) => void): () => void;

    // Vsid
    readonly vsid: string | undefined;

    // VideoTrack
    readonly videoTrack: VideoTrack | undefined;
    readonly videoTracks: ReadonlyArray<VideoTrack> | undefined;
    onCurrentVideoTrackChange(callback: (videoTrack?: VideoTrack) => void): () => void;
    onVideoTracksChange(callback: (videoTrack?: ReadonlyArray<VideoTrack>) => void): () => void;
    setVideoTrack(videoTrack?: VideoTrack): void;

    // PlaybackRate
    readonly playbackRate: number | undefined;
    readonly playbackRateRange: PlaybackRateRange | undefined;
    onPlaybackRateChange(callback: (playbackRate?: number) => void): () => void;
    onPlaybackRateRangeChange(
        callback: (playbackRateRange?: PlaybackRateRange) => void
    ): () => void;
    setPlaybackRate(rate: number): void;

    // BufferedRange
    readonly bufferedRanges: ReadonlyArray<TimeRange> | undefined;
    onBufferedRangesChange(
        callback: (bufferedRange?: ReadonlyArray<TimeRange>) => void
    ): () => void;

    // VideoType
    readonly videoType: VideoType | undefined;

    // IsFullscreen
    readonly isFullscreen: boolean;

    // Position
    moveToContainer(container: HTMLElement): void;

    // Drm
    readonly drmSystem: DrmSystemName | undefined;
    readonly isDrmSupported: boolean;

    // UtcStartTime
    readonly utcStartTime: number | undefined;
    onUtcStartTimeChange(callback: (utcStartTime?: number) => void): () => void;

    // Ad
    readonly adState: AdState | undefined;
    onAdStateChanged(callback: (isPlaying: AdPlayingState) => void): () => void;
    onAdPodStart(callback: () => void): () => void;
    onAdPodEnd(callback: () => void): () => void;
    onAdPodSkip(callback: () => void): () => void;
    onAdLoaded(callback: (adType?: AdType) => void): () => void;
    onAdError(callback: () => void): () => void;
    onAdPodClick(callback: () => void): () => void;

    // Subtitles
    readonly currentSubtitles: string[] | undefined;
    onCurrentSubtitlesChange(callback: (subtitles?: string[]) => void): () => void;
    setNativeSubtitlesVisibility(isVisible: boolean): void;

    // DVR
    readonly dvrStartTime: number | undefined;
    readonly dvrEndTime: number | undefined;
    readonly dvrDuration: number | undefined;
    readonly utcPosition: number | undefined;
    onDVRTimeChanged(
        callback: (props: { startTime?: number; endTime?: number }) => void
    ): () => void;
    onDVRDurationChanged(callback: (duration?: number) => void): () => void;
    onUTCPositionChanged(callback: (duration: number) => void): () => void;

    // Debug
    requestDebugInfo?(): Promise<unknown>;

    // Destroy
    destroy(): void;

    // PictureInPicture
    readonly isPictureInPicture: boolean;
    onPictureInPictureStateChanged(callback: (isPictureInPicture: boolean) => void): () => void;
    setIsPictureInPictureAllowed(isAllowed: boolean): void;
}

export type FinishEventListener = (context?: FinishEventContext) => void;

interface FinishEventContext extends FilmoscopeEndedEventContext {}

export interface FilmoscopeAdapter extends BasePlayerAdapter {
    type: 'filmoscope';
    readonly pureInstance:
        | FilmoscopeApi
        | LegacySDKPlayback<string, Partial<RequiredParamsForAnyPlayback>>;
    autoplayPolicy: AutoplayPolicy | undefined;
    moveToContainer(container: HTMLElement): void;
    logEvent(eventName: string, details?: Record<string, unknown>): void;
    onSeeking(callback: (params: SeekingPositions) => void): () => void;
    onSeeked(callback: (params: SeekedPositions) => void): () => void;
    setGetSource(getSource: FetchSourceCallback, contentId?: string): void;
    setPlayingContentId(contentId: string | null): void;
    setContent(contentType: string, payload: SDKContentParams<RequiredParamsForAnyPlayback>): void;
}

export interface PlayerApiError {
    name?: string;
    message: Error['message'];
    code: string | number;
    isFatal: boolean;
    details: unknown;
    stack?: Error['stack'];
    vsid?: string;
    messageDetails?: {
        title: string | undefined;
        message: string | undefined;
        helpLink: string | null | undefined;
        helpLinkButtonText: string;
    };
    isDrmError?: boolean;
    category: string;
    originalError?: unknown;
}

export enum PlayingState {
    play = 'play',
    pause = 'pause',
    buffering = 'buffering',
    end = 'end',
}

export interface VideoSize {
    readonly height: number;
    readonly width: number;
}

export interface SeekingPositions extends SeekedPositions {}

export interface SeekedPositions {
    oldPositionSec: number;
    newPositionSec: number;
}

interface Track {
    name: string;
    value: string;
}

export interface TextTrack extends Track {
    lang: string;
}

export interface AudioTrack extends Track {
    lang: string;
    bitrate?: number;
}

export interface VideoTrack extends Track {
    bitrate: number;
    width?: number;
    height?: number;
    auto?: boolean;
}

export interface PlaybackRateRange {
    max: number;
    min: number;
}

export interface TimeRange {
    start: number;
    end: number;
}

export enum VideoType {
    live = 'live',
    event = 'event',
    vod = 'vod',
}

export type DrmSystemName = 'fairplay' | 'playready' | 'widevine' | 'clear';

interface AdState {
    state: AdPlayingState;
    type: AdType | undefined;
}

enum AdPlayingState {
    playing = 1,
    notPlaying = 2,
}

enum AdType {
    preroll = 'preroll',
    midroll = 'midroll',
    postPauseroll = 'postPauseroll',
    postroll = 'postroll',
    inroll = 'inroll',
}

export enum PlayerErrors {
    loadingPlayerError = 'loadingPlayerError',
}
